import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, FileText } from 'lucide-react';
import { documentService } from '../../../../services/documentos/documentService';

export const UploadDocumentModal = ({
  isOpen,
  onClose,
  product,
  documentTypes = [],
  onSuccess,
}) => {
  const [documentType, setDocumentType] = useState('');
  const [description, setDescription] = useState('');
  const [addToFamily, setAddToFamily] = useState(false);
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!documentType.trim()) {
      setError('Document type is required');
      return;
    }
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('ProductID', String(product.id));
      formData.append('ProductTypeName', product.product_type || '');
      formData.append('ProductCode', product.name || '');
      formData.append('ProductManufacturer', product.manufacturer || '');
      formData.append('DocumentType', documentType);
      formData.append('Description', description);
      formData.append('document', file);

      if (addToFamily && product.family) {
        formData.append('ProductFamily', product.family);
      }

      await documentService.uploadProductDocument(formData, ({ percent }) => setUploadProgress(percent));
      onSuccess?.();
      handleClose();
    } catch (err) {
      // Mostrar mensaje del servidor para depuración (400/500)
      const serverMsg = err.response?.data?.message ?? err.response?.data?.error ?? err.message;
      const status = err.response?.status;
      const debugMsg = status ? `[${status}] ${serverMsg}` : serverMsg;
      console.error('[UploadDocument] Error:', { err, response: err.response });
      setError(debugMsg || 'Failed to upload document');
      setUploadProgress(0);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setDocumentType('');
      setDescription('');
      setAddToFamily(false);
      setFile(null);
      setError(null);
      setUploadProgress(0);
      onClose();
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    setFile(selectedFile || null);
  };

  if (!isOpen || !product) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-800 rounded-2xl border border-gray-700 shadow-xl w-full max-w-lg overflow-hidden flex flex-col relative z-[101]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
            <FileText className="w-6 h-6 text-gray-400" />
            Upload Document
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {error && (
              <div className="rounded-lg border border-red-800 bg-red-900/20 p-4">
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            {/* Document Type */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Document Type <span className="text-red-400">*</span>
              </label>
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isSubmitting}
                required
              >
                <option value="">Select document type</option>
                {documentTypes.map((dt) => (
                  <option key={dt.id} value={dt.name}>
                    {dt.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows="3"
                className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Optional description"
                disabled={isSubmitting}
              />
            </div>

            {/* File input */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                File <span className="text-red-400">*</span>
              </label>
              <input
                type="file"
                accept=".pdf,application/pdf,image/*,.doc,.docx"
                onChange={handleFileChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-600 file:text-white file:cursor-pointer hover:file:bg-blue-700"
                disabled={isSubmitting}
                required
              />
              {file && (
                <p className="mt-1 text-sm text-gray-400 truncate">{file.name}</p>
              )}
            </div>

            {/* Add to Family */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="addToFamily"
                checked={addToFamily}
                onChange={(e) => setAddToFamily(e.target.checked)}
                className="rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
                disabled={isSubmitting}
              />
              <label htmlFor="addToFamily" className="text-sm text-gray-300 cursor-pointer">
                Add to Family
              </label>
            </div>
            {product.family && addToFamily && (
              <p className="text-xs text-gray-500">
                Document will be associated with family &quot;{product.family}&quot;
              </p>
            )}

            {/* Barra de progreso de subida */}
            {isSubmitting && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-400">
                  <span>Subiendo archivo...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-700 overflow-hidden">
                  <div
                    className="h-full bg-blue-600 transition-all duration-300 ease-out"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg border border-blue-600 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              disabled={isSubmitting}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Upload
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
